import { defineComponent as _defineComponent } from 'vue';
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, unref as _unref, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, createTextVNode as _createTextVNode, createVNode as _createVNode, vModelText as _vModelText, withDirectives as _withDirectives, withModifiers as _withModifiers } from "vue";
const _hoisted_1 = { class: "flex min-w-0 items-center gap-2" };
const _hoisted_2 = ["src", "alt"];
const _hoisted_3 = { class: "text-sm font-bold text-white" };
const _hoisted_4 = { class: "flex flex-col gap-1 sm:items-end" };
const _hoisted_5 = { class: "flex items-center gap-2" };
const _hoisted_6 = ["href"];
const _hoisted_7 = ["onClick"];
const _hoisted_8 = {
    key: 0,
    class: "text-xs text-gray-500"
};
const _hoisted_9 = { class: "flex flex-1 items-start justify-center px-4 py-10" };
const _hoisted_10 = ["aria-label"];
const _hoisted_11 = { class: "text-sm text-gray-500" };
const _hoisted_12 = { class: "mb-6 flex flex-col gap-1.5" };
const _hoisted_13 = { class: "text-2xl font-bold text-gray-900" };
const _hoisted_14 = { class: "text-sm text-gray-500" };
const _hoisted_15 = { class: "flex min-w-0 flex-col gap-1" };
const _hoisted_16 = { class: "truncate font-semibold text-gray-900" };
const _hoisted_17 = {
    key: 0,
    class: "flex items-center gap-1 text-xs text-gray-500"
};
const _hoisted_18 = { class: "mb-6 flex flex-col gap-2.5" };
const _hoisted_19 = { class: "text-sm font-semibold text-gray-900" };
const _hoisted_20 = {
    key: 0,
    class: "text-xs text-red-500",
    role: "alert"
};
const _hoisted_21 = {
    key: 1,
    class: "text-xs text-gray-400"
};
const _hoisted_22 = { class: "mb-8 flex flex-col gap-2" };
const _hoisted_23 = {
    for: "review-comment",
    class: "text-sm font-semibold text-gray-900"
};
const _hoisted_24 = ["placeholder"];
const _hoisted_25 = { class: "self-end text-xs text-gray-400" };
const _hoisted_26 = {
    key: 0,
    class: "text-xs text-red-500",
    role: "alert"
};
const _hoisted_27 = ["disabled", "aria-busy"];
const _hoisted_28 = {
    key: 0,
    class: "h-4 w-4 animate-spin",
    xmlns: "http://www.w3.org/2000/svg",
    fill: "none",
    viewBox: "0 0 24 24",
    "aria-hidden": "true"
};
const _hoisted_29 = {
    key: 1,
    class: "mt-3 text-center text-xs text-red-500",
    role: "alert"
};
const _hoisted_30 = { class: "text-xs text-gray-400" };
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import posthog from "posthog-js";
import { i18n, resolveAppLocale } from "@/i18n";
import { usePublicReview } from "@/domains/reviews/composables";
import { ReviewInvitationRequestError } from "@/domains/reviews/services/review-invitation-client.service";
import StarRatingSelector from "@/domains/reviews/components/StarRatingSelector.vue";
import ReviewStateDisplay from "@/domains/reviews/components/ReviewStateDisplay.vue";
import { normalizePublicBrandProfile, } from "@/shared/branding/public-brand";
import { appLanguageOptions } from "@/shared/language/language-preferences";
export default /*@__PURE__*/ _defineComponent({
    __name: 'ReviewPage',
    setup(__props) {
        const route = useRoute();
        const { t, locale } = useI18n();
        const { reviewContext, pageState, isSubmittingReview, reviewError, loadReviewContext, submitReview, } = usePublicReview();
        const rating = ref(null);
        const comment = ref("");
        const ratingError = ref(null);
        const commentError = ref(null);
        const languageOptions = appLanguageOptions;
        const invitationId = computed(() => String(route.params.invitationId ?? ""));
        const accessGrant = ref("");
        const brand = computed(() => normalizePublicBrandProfile(reviewContext.value?.brand));
        const brandInitial = computed(() => brand.value.name.trim().charAt(0).toUpperCase());
        const supportEmail = computed(() => brand.value.support.email);
        const softBorderColor = computed(() => `${brand.value.colors.accent}66`);
        const subtleBackground = computed(() => `${brand.value.colors.accent}1a`);
        const effectiveCommunicationLocale = computed(() => reviewContext.value?.effective_locale || reviewContext.value?.locale || null);
        const languageSource = computed(() => reviewContext.value?.language_source || null);
        const languageProvenanceText = computed(() => {
            if (!effectiveCommunicationLocale.value || !languageSource.value)
                return "";
            return t("language.communication_locale", {
                locale: effectiveCommunicationLocale.value,
                source: t(`language.sources.${languageSource.value}`),
            });
        });
        function parsePrefillRating(raw) {
            const n = Number(raw);
            if (!Number.isInteger(n) || n < 1 || n > 5)
                return null;
            return n;
        }
        function parseHashParams(hash) {
            const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
            return {
                accessGrant: params.get("access-grant") ?? "",
                rating: parsePrefillRating(params.get("rating")),
            };
        }
        function formatDate(dateStr) {
            try {
                return new Intl.DateTimeFormat(locale.value, {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                }).format(new Date(dateStr));
            }
            catch {
                return dateStr;
            }
        }
        async function handleSubmit() {
            ratingError.value = null;
            commentError.value = null;
            if (!rating.value) {
                ratingError.value = t("reviews.validation.rating_required");
                return;
            }
            if (comment.value.length > 1000) {
                commentError.value = t("reviews.validation.comment_max");
                return;
            }
            posthog.capture("review_submission_started", {
                rating: rating.value,
                has_comment: comment.value.length > 0,
            });
            try {
                await submitReview(invitationId.value, accessGrant.value, {
                    rating: rating.value,
                    comment: comment.value,
                });
                posthog.capture("review_submission_completed", { rating: rating.value });
            }
            catch (err) {
                posthog.capture("review_submission_failed", {
                    error_code: resolveAnalyticsErrorCode(err),
                });
            }
        }
        function resolveAnalyticsErrorCode(error) {
            if (error instanceof ReviewInvitationRequestError) {
                const body = error.body;
                if (body && typeof body === "object") {
                    const code = body.code;
                    if (typeof code === "string" && code.trim())
                        return code.trim();
                }
                return `http_${error.status}`;
            }
            return "review_submission_failed";
        }
        function applyPublicLocale(value) {
            const next = resolveAppLocale(value);
            locale.value = next;
            i18n.global.locale.value = next;
            document.documentElement.lang = next;
        }
        function languageButtonStyle(value) {
            const active = locale.value === value;
            return active
                ? {
                    backgroundColor: brand.value.colors.primary,
                    color: "#ffffff",
                }
                : {
                    color: brand.value.colors.text,
                };
        }
        onMounted(async () => {
            const { accessGrant: grant, rating: prefillRating } = parseHashParams(window.location.hash);
            accessGrant.value = grant;
            rating.value = prefillRating;
            // The access grant must never persist visibly in the URL bar once read (it's a single-use,
            // anonymous capability token carried only in the fragment for this initial load). Preserve
            // history.state (Vue Router stores its own navigation bookkeeping there) rather than nulling
            // it out — see https://router.vuejs.org/guide/migration/#Usage-of-history-state.
            window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
            await loadReviewContext(invitationId.value, accessGrant.value);
            const publicLocale = reviewContext.value?.effective_locale ?? reviewContext.value?.locale;
            if (publicLocale)
                applyPublicLocale(publicLocale);
            posthog.capture("review_page_loaded", { prefill_rating: prefillRating });
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", {
                class: "flex min-h-screen flex-col",
                style: _normalizeStyle({
                    backgroundColor: brand.value.colors.background,
                    color: brand.value.colors.text,
                    fontFamily: brand.value.fontFamily,
                })
            }, [
                _createElementVNode("header", {
                    class: "flex min-h-16 items-center justify-between gap-4 border-b bg-white px-5 py-3 sm:px-10",
                    style: _normalizeStyle({ borderColor: softBorderColor.value })
                }, [
                    _createElementVNode("div", _hoisted_1, [
                        (brand.value.logoUrl)
                            ? (_openBlock(), _createElementBlock("img", {
                                key: 0,
                                src: brand.value.logoUrl,
                                alt: _ctx.$t('reviews.accessibility.brand_logo', { brand: brand.value.name }),
                                class: "h-9 w-9 rounded-lg object-contain"
                            }, null, 8, _hoisted_2))
                            : (_openBlock(), _createElementBlock("div", {
                                key: 1,
                                class: "flex h-9 w-9 items-center justify-center rounded-lg",
                                style: _normalizeStyle({ backgroundColor: brand.value.colors.primary }),
                                "aria-hidden": "true"
                            }, [
                                _createElementVNode("span", _hoisted_3, _toDisplayString(brandInitial.value), 1)
                            ], 4)),
                        _createElementVNode("span", {
                            class: "truncate text-lg font-bold",
                            style: _normalizeStyle({ color: brand.value.colors.text })
                        }, _toDisplayString(brand.value.name), 5)
                    ]),
                    _createElementVNode("div", _hoisted_4, [
                        _createElementVNode("div", _hoisted_5, [
                            (supportEmail.value)
                                ? (_openBlock(), _createElementBlock("a", {
                                    key: 0,
                                    href: `mailto:${supportEmail.value}`,
                                    class: "rounded-lg border px-3.5 py-2 text-sm",
                                    style: _normalizeStyle({ borderColor: softBorderColor.value, color: brand.value.colors.text })
                                }, _toDisplayString(_ctx.$t("reviews.shell.support")), 13, _hoisted_6))
                                : _createCommentVNode("", true),
                            _createElementVNode("div", {
                                class: "flex rounded-lg border p-1",
                                style: _normalizeStyle({ borderColor: softBorderColor.value })
                            }, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(languageOptions), (option) => {
                                    return (_openBlock(), _createElementBlock("button", {
                                        key: option.value,
                                        type: "button",
                                        class: "rounded-md px-2.5 py-1 text-xs font-semibold",
                                        style: _normalizeStyle(languageButtonStyle(option.value)),
                                        onClick: ($event) => (applyPublicLocale(option.value))
                                    }, _toDisplayString(option.label), 13, _hoisted_7));
                                }), 128))
                            ], 4)
                        ]),
                        (languageProvenanceText.value)
                            ? (_openBlock(), _createElementBlock("p", _hoisted_8, _toDisplayString(languageProvenanceText.value), 1))
                            : _createCommentVNode("", true)
                    ])
                ], 4),
                _createElementVNode("main", _hoisted_9, [
                    (_unref(pageState) === 'loading')
                        ? (_openBlock(), _createElementBlock("div", {
                            key: 0,
                            class: "flex w-full max-w-[560px] flex-col items-center gap-4 py-16",
                            role: "status",
                            "aria-label": _ctx.$t('reviews.accessibility.loading')
                        }, [
                            (_openBlock(), _createElementBlock("svg", {
                                class: "h-8 w-8 animate-spin",
                                style: _normalizeStyle({ color: brand.value.colors.primary }),
                                xmlns: "http://www.w3.org/2000/svg",
                                fill: "none",
                                viewBox: "0 0 24 24",
                                "aria-hidden": "true"
                            }, [...(_cache[2] || (_cache[2] = [
                                    _createElementVNode("circle", {
                                        class: "opacity-25",
                                        cx: "12",
                                        cy: "12",
                                        r: "10",
                                        stroke: "currentColor",
                                        "stroke-width": "4"
                                    }, null, -1),
                                    _createElementVNode("path", {
                                        class: "opacity-75",
                                        fill: "currentColor",
                                        d: "M4 12a8 8 0 018-8v8z"
                                    }, null, -1)
                                ]))], 4)),
                            _createElementVNode("p", _hoisted_11, _toDisplayString(_ctx.$t("reviews.accessibility.loading")), 1)
                        ], 8, _hoisted_10))
                        : (_unref(pageState) !== 'eligible')
                            ? (_openBlock(), _createBlock(ReviewStateDisplay, {
                                key: 1,
                                state: _unref(pageState),
                                "support-email": supportEmail.value,
                                "aria-live": "polite"
                            }, null, 8, ["state", "support-email"]))
                            : (_openBlock(), _createElementBlock("form", {
                                key: 2,
                                class: "w-full max-w-[560px] rounded-2xl border bg-white p-10 shadow-sm",
                                style: _normalizeStyle({ borderColor: softBorderColor.value }),
                                novalidate: "",
                                onSubmit: _withModifiers(handleSubmit, ["prevent"])
                            }, [
                                _createElementVNode("div", _hoisted_12, [
                                    _createElementVNode("h1", _hoisted_13, _toDisplayString(_ctx.$t("reviews.submission.title")), 1),
                                    _createElementVNode("p", _hoisted_14, _toDisplayString(_ctx.$t("reviews.submission.subtitle")), 1)
                                ]),
                                (_unref(reviewContext)?.service_name)
                                    ? (_openBlock(), _createElementBlock("div", {
                                        key: 0,
                                        class: "mb-6 flex items-center gap-4 rounded-xl border bg-slate-50 p-4",
                                        style: _normalizeStyle({ borderColor: softBorderColor.value })
                                    }, [
                                        _createElementVNode("div", {
                                            class: "flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl",
                                            style: _normalizeStyle({ backgroundColor: subtleBackground.value })
                                        }, [
                                            (_openBlock(), _createElementBlock("svg", {
                                                xmlns: "http://www.w3.org/2000/svg",
                                                class: "h-5 w-5",
                                                style: _normalizeStyle({ color: brand.value.colors.primary }),
                                                viewBox: "0 0 24 24",
                                                fill: "none",
                                                stroke: "currentColor",
                                                "stroke-width": "2",
                                                "stroke-linecap": "round",
                                                "stroke-linejoin": "round",
                                                "aria-hidden": "true"
                                            }, [...(_cache[3] || (_cache[3] = [
                                                    _createElementVNode("path", { d: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" }, null, -1),
                                                    _createElementVNode("circle", {
                                                        cx: "12",
                                                        cy: "9",
                                                        r: "2.5"
                                                    }, null, -1)
                                                ]))], 4))
                                        ], 4),
                                        _createElementVNode("div", _hoisted_15, [
                                            _createElementVNode("p", _hoisted_16, _toDisplayString(_unref(reviewContext).service_name), 1),
                                            (_unref(reviewContext).service_date)
                                                ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                                                    _createElementVNode("span", null, _toDisplayString(formatDate(_unref(reviewContext).service_date)), 1)
                                                ]))
                                                : _createCommentVNode("", true)
                                        ])
                                    ], 4))
                                    : _createCommentVNode("", true),
                                _createElementVNode("div", _hoisted_18, [
                                    _createElementVNode("label", _hoisted_19, [
                                        _createTextVNode(_toDisplayString(_ctx.$t("reviews.submission.rating_label")) + " ", 1),
                                        _cache[4] || (_cache[4] = _createElementVNode("span", {
                                            class: "text-red-500",
                                            "aria-hidden": "true"
                                        }, "*", -1))
                                    ]),
                                    _createVNode(StarRatingSelector, {
                                        modelValue: rating.value,
                                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((rating).value = $event))
                                    }, null, 8, ["modelValue"]),
                                    (ratingError.value)
                                        ? (_openBlock(), _createElementBlock("p", _hoisted_20, _toDisplayString(ratingError.value), 1))
                                        : (_openBlock(), _createElementBlock("p", _hoisted_21, _toDisplayString(_ctx.$t("reviews.submission.rating_hint")), 1))
                                ]),
                                _createElementVNode("div", _hoisted_22, [
                                    _createElementVNode("label", _hoisted_23, _toDisplayString(_ctx.$t("reviews.submission.comment_label")), 1),
                                    _withDirectives(_createElementVNode("textarea", {
                                        id: "review-comment",
                                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((comment).value = $event)),
                                        class: "min-h-[120px] w-full resize-none rounded-lg border bg-white p-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2",
                                        style: _normalizeStyle({ borderColor: softBorderColor.value, '--tw-ring-color': softBorderColor.value }),
                                        placeholder: _ctx.$t('reviews.submission.comment_placeholder'),
                                        maxlength: "1000"
                                    }, null, 12, _hoisted_24), [
                                        [_vModelText, comment.value]
                                    ]),
                                    _createElementVNode("p", _hoisted_25, _toDisplayString(1000 - comment.value.length) + " / 1000 " + _toDisplayString(_ctx.$t("reviews.submission.chars_remaining")), 1),
                                    (commentError.value)
                                        ? (_openBlock(), _createElementBlock("p", _hoisted_26, _toDisplayString(commentError.value), 1))
                                        : _createCommentVNode("", true)
                                ]),
                                _createElementVNode("button", {
                                    type: "submit",
                                    class: "flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60",
                                    style: _normalizeStyle({ backgroundColor: brand.value.colors.primary }),
                                    disabled: _unref(isSubmittingReview),
                                    "aria-busy": _unref(isSubmittingReview)
                                }, [
                                    (_unref(isSubmittingReview))
                                        ? (_openBlock(), _createElementBlock("svg", _hoisted_28, [...(_cache[5] || (_cache[5] = [
                                                _createElementVNode("circle", {
                                                    class: "opacity-25",
                                                    cx: "12",
                                                    cy: "12",
                                                    r: "10",
                                                    stroke: "currentColor",
                                                    "stroke-width": "4"
                                                }, null, -1),
                                                _createElementVNode("path", {
                                                    class: "opacity-75",
                                                    fill: "currentColor",
                                                    d: "M4 12a8 8 0 018-8v8z"
                                                }, null, -1)
                                            ]))]))
                                        : _createCommentVNode("", true),
                                    _createTextVNode(" " + _toDisplayString(_unref(isSubmittingReview) ? _ctx.$t("reviews.submission.submitting") : _ctx.$t("reviews.submission.submit")), 1)
                                ], 12, _hoisted_27),
                                (_unref(reviewError) && !_unref(isSubmittingReview))
                                    ? (_openBlock(), _createElementBlock("p", _hoisted_29, _toDisplayString(_unref(reviewError)), 1))
                                    : _createCommentVNode("", true)
                            ], 36))
                ]),
                _createElementVNode("footer", {
                    class: "flex h-12 items-center justify-center border-t bg-white",
                    style: _normalizeStyle({ borderColor: softBorderColor.value })
                }, [
                    _createElementVNode("p", _hoisted_30, _toDisplayString(brand.value.name), 1)
                ], 4)
            ], 4));
        };
    }
});
